import { WorklistColumnId } from 'generated/graphql';
import { CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY } from '../Worklist/WorklistPageV2/constants';

export const WorklistGlobalFilters = Object.freeze({
  PatientSearch: 'patientSearch',
  Views: 'views',
});

export type WorklistGlobalFiltersType =
  (typeof WorklistGlobalFilters)[keyof typeof WorklistGlobalFilters];

export const WorklistFilters = Object.freeze({
  AccessionNumber: WorklistColumnId.AccessionNumber,
  Age: WorklistColumnId.Age,
  BodyPart: WorklistColumnId.BodyPart,
  CustomStatus: WorklistColumnId.CustomStatus,
  HasDraftReport: WorklistColumnId.HasDraftReport,
  Modality: WorklistColumnId.Modality,
  Mrn: WorklistColumnId.Mrn,
  PatientDob: WorklistColumnId.PatientDob,
  PatientName: WorklistColumnId.PatientName,
  PatientSearch: WorklistGlobalFilters.PatientSearch,
  PatientSex: WorklistColumnId.PatientSex,
  PatientType: WorklistColumnId.PatientType,
  Priority: WorklistColumnId.Priority,
  Radiologist: WorklistColumnId.Radiologist,
  ReferringPhysician: WorklistColumnId.ReferringPhysician,
  SignedBy: WorklistColumnId.SignedBy,
  Site: WorklistColumnId.Site,
  Status: WorklistColumnId.Status,
  StudyDate: WorklistColumnId.StudyDate,
  StudyDescription: WorklistColumnId.StudyDescription,
  SubmittedAt: WorklistColumnId.SubmittedAt,
  Views: WorklistGlobalFilters.Views,
  [CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY]: CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY,
  QcStatus: WorklistColumnId.QcStatus,
  ExamStatus: WorklistColumnId.ExamStatus,
  ClaimedBy: 'claimedBy', // This is only available via the canned My Queue worklist
  ExamCode: WorklistColumnId.ExamCode,
  Specialty: WorklistColumnId.Specialty,
  ReportExtendedStatus: WorklistColumnId.ReportExtendedStatus,
  CriticalResult: WorklistColumnId.CriticalResult,
});

export const WorklistFilterToColumnName: Record<string, string> = {
  accessionNumber: 'ACC #',
  bodyPart: 'Body Part',
  claimedBy: 'Assigned to',
  claimedByPartial: 'Assigned to (last name)',
  customStatus: 'Workflow Status',
  examStatus: 'Exam Status',
  hasDraftReport: 'Has Draft',
  modality: 'Modality',
  mrn: 'MRN #',
  patientDob: 'DOB',
  patientLastName: 'Patient Name',
  patientName: 'Patient Name',
  patientSex: 'Gender',
  patientType: 'Patient Type',
  priority: 'Priority',
  priorityCode: 'Priority',
  radiologist: 'Assigned to',
  referringPhysicianPartial: 'Ordering Physician',
  referringPhysician: 'Ordering Physician',
  site: 'Site',
  status: 'Status',
  studyDate: 'Exam Date',
  studyDescription: 'Exam Description',
  submitted_at: 'Submitted At',
  submittedAt: 'Submitted At',
  signedBy: 'Signed By',
  specialty: 'Specialty',
  reportExtendedStatus: 'Report Status',
  criticalResult: 'Critical Workflow',
};

export const WorklistFilterSourceName = {
  BodyPart: 'body_part',
  CustomStatus: 'custom_status',
  HasDraftReport: 'has_draft_report',
  Modality: 'modality',
  PatientName: 'patient_last_name',
  PatientSex: 'patient_sex',
  PatientType: 'patient_type',
  Priority: 'priority',
  Radiologist: 'claimed_by',
  ReferringPhysician: 'referring_physician',
  Site: 'site',
  Status: 'status',
  StudyDescription: 'study_description',
  QcStatus: 'qc_status',
  ExamCode: 'exam_code',
  ReportExtendedStatus: 'report_extended_status',
} as const;

export const WorklistFilterSourceToName = {
  body_part: 'Body Part',
  claimed_by: 'Radiologist',
  custom_status: 'Workflow Status',
  exam_code: 'Exam Code',
  has_draft_report: 'Draft',
  modality: 'Modality',
  patient_last_name: 'Patient Name',
  patient_sex: 'Patient Sex',
  patient_type: 'Patient Type',
  priority_code: 'Priority',
  referring_physician: 'Ordering Physician',
  site: 'Site',
  status: 'Status',
  study_description: 'Study Description',
  submitted_at: 'Submitted At',
  submittedAt: 'Submitted At',
} as const;

export type WorklistFilterSourceNameType =
  (typeof WorklistFilterSourceName)[keyof typeof WorklistFilterSourceName];

export type WorklistFiltersType = (typeof WorklistFilters)[keyof typeof WorklistFilters];

export const FilterVariant = Object.freeze({
  MultiSelect: 'multiselect',
  DateRange: 'daterange',
  Toggle: 'toggle',
  NumberRange: 'numberrange',
  Text: 'text',
});

export type FilterVariantType = (typeof FilterVariant)[keyof typeof FilterVariant];

export type BaseFilterType = {
  label: string;
  hidden?: boolean;
  isLoading?: boolean;
};

export type MultiSelectFilterItemType = {
  value: string;
  label: string;
  count?: number;
  defaultSelected?: boolean;
};

export type ToggleFilterType = BaseFilterType & {
  type: typeof FilterVariant.Toggle;
  defaultValue: string;
};

export type MultiSelectFilterType = BaseFilterType & {
  type: typeof FilterVariant.MultiSelect;
  isSearchable?: boolean;
  source: WorklistFilterSourceNameType | '';
  items?: MultiSelectFilterItemType[];
  isBoolean?: boolean; // Must only have 1 selected, if not, it's like no filter is added};,
  isLoading?: boolean;
  formatItems?: (items: MultiSelectFilterItemType[]) => MultiSelectFilterItemType[];
  forceLockDefaultSelection?: boolean;
};

export type DateRangeFilterType = BaseFilterType & {
  type: typeof FilterVariant.DateRange;
  minDate?: Date;
  maxDate?: Date;
  supportsRelativeDates?: boolean;
};

export type NumberRangeFilterType = BaseFilterType & {
  type: typeof FilterVariant.NumberRange;
};

export type TextFilterType = BaseFilterType & {
  type: typeof FilterVariant.Text;
  placeholder?: string;
};

export type FilterType =
  | MultiSelectFilterType
  | DateRangeFilterType
  | ToggleFilterType
  | NumberRangeFilterType
  | TextFilterType;

export type FilterValueType = string[];

export type FilterStateType = Partial<Record<WorklistFiltersType, FilterValueType>>;

export type FilterDefinitionType = Partial<Record<WorklistFiltersType, FilterType>>;

export type ProcessedDateFilterValueType = [Nullable<Date>, Nullable<Date>];

export type ProcessedBooleanFilterValueType = Nullable<boolean>;

export type ProcessedMultiSelectFilterValueType = ReadonlyArray<string>;

export type ProcessedNumberRangeFilterValueType = [Nullable<number>, Nullable<number>];

export type ProcessedTextFilterValueType = Nullable<string | ReadonlyArray<string>>;

export type ProcessedFilterValueType =
  | ProcessedDateFilterValueType
  | ProcessedBooleanFilterValueType
  | ProcessedMultiSelectFilterValueType
  | ProcessedTextFilterValueType
  | ProcessedNumberRangeFilterValueType;

export type ProcessedFilterStateType = {
  [key in WorklistFiltersType]?: ProcessedFilterValueType;
};

export type WorklistSurfaceSearchValues = {
  [key: string]: string;
};

export type FilterStateBySurface = {
  [key: string]: FilterStateType;
};

export type NumberRangeTupleString = [Nullable<string>, Nullable<string>];
export type NumberRangeTuple = [Nullable<number>, Nullable<number>];

export type FilterSelectedValueMap = {
  [FilterVariant.MultiSelect]: string[];
  [FilterVariant.DateRange]: [Nullable<string>, Nullable<string>];
  [FilterVariant.NumberRange]: [Nullable<number>, Nullable<number>];
  [FilterVariant.Text]: string[];
};

export type FilterSelectedValueType<T extends FilterType> = T extends {
  type: infer Variant;
}
  ? Variant extends keyof FilterSelectedValueMap
    ? FilterSelectedValueMap[Variant]
    : string[]
  : string[];

export type DialogBaseFilter = {
  id: string;
  label: string;
  type: FilterVariantType;
  source?: string;
  isSearchable?: boolean;
  selectedValues?: FilterSelectedValueType<FilterType>;
  isOpen?: boolean;
  tooltip?: React.ReactNode | string;
};

export type DialogFiltersMap = Record<string, DialogBaseFilter>;

export const WorklistFilterReverseSourceName = {
  modality: 'modality',
  patient_type: 'patientType',
  patient_sex: 'patientSex',
  study_description: 'studyDescription',
  body_part: 'bodyPart',
  claimed_by: 'radiologist',
  custom_status: 'customStatus',
  status: 'status',
  site: 'site',
  has_draft_report: 'hasDraftReport',
  patient_last_name: 'patientName',
  referring_physician: 'referringPhysician',
  priority: 'priority',
} as const;

export type DateType = Nullable<Date>;
